import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Calendar, AlertTriangle, ArrowRight, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUsage, refreshUsage } from "@/hooks/useUsage";
import ConfirmModal from "./ConfirmModal";

const PLAN_LABEL = {
    starter: { name: "Starter", color: "text-text-primary", bg: "bg-surface-hover/40", border: "border-border" },
    growth:  { name: "Growth",  color: "text-primary-c",    bg: "bg-primary/15",       border: "border-primary/30" },
    scale:   { name: "Scale",   color: "text-warning-c",    bg: "bg-warning/15",       border: "border-warning/30" },
};

const STATUS_LABEL = {
    active:    { text: "Активна",      color: "text-success-c", Icon: CheckCircle2 },
    trial:     { text: "Тріал",        color: "text-primary-c", Icon: CheckCircle2 },
    cancelled: { text: "Скасовується", color: "text-warning-c", Icon: AlertTriangle },
    expired:   { text: "Завершена",    color: "text-error-c",   Icon: XCircle },
};

const fmtDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("uk-UA", {
            day: "numeric", month: "long", year: "numeric",
        });
    } catch { return null; }
};

const ProgressBar = ({ label, used, limit }) => {
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const color = pct >= 90 ? "bg-error" : pct >= 70 ? "bg-warning" : "bg-primary";
    return (
        <div data-testid={`billing-limit-${label}`}>
            <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-secondary-c">{label}</span>
                <span className="font-mono text-faint-c">{used} / {limit}</span>
            </div>
            <div className="h-1.5 rounded-full bg-surface-hover/40 overflow-hidden">
                <div className={`h-full ${color} transition-all duration-300`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

const BillingSection = ({ user }) => {
    const navigate = useNavigate();
    const { usage } = useUsage();
    const loading = !usage;
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);

    const plan = user?.plan;
    const planMeta = PLAN_LABEL[plan];
    const status = user?.plan_status;
    const statusMeta = STATUS_LABEL[status];
    const periodEnd = fmtDate(user?.plan_period_end || usage?.current_period_end);

    const hasActiveSub = plan && status && status !== "expired";
    const isCancelling = status === "cancelled";

    const handleCancel = async () => {
        setCancelLoading(true);
        try {
            await api.post("/plan/cancel-subscription");
            toast.success("Підписку скасовано. Активна до кінця періоду.");
            await refreshUsage();
            setConfirmCancel(false);
            window.dispatchEvent(new Event("user:refresh"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Не вдалося скасувати підписку");
        } finally {
            setCancelLoading(false);
        }
    };

    const limits = usage?.limits || [];
    const bars = limits.filter(b => b.type === "consumable" || b.type === "cap");

    return (
        <div className="space-y-5" data-testid="billing-section-content">
            {!hasActiveSub ? (
                <div className="rounded-lg border border-warning/30 bg-warning/10 p-4" data-testid="billing-no-sub">
                    <div className="flex items-start gap-3">
                        <AlertTriangle size={18} className="text-warning-c mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <div className="font-semibold text-sm text-text-primary">Активна підписка відсутня</div>
                            <p className="mt-1 text-xs text-secondary-c">
                                Оформіть тариф щоб розблокувати генерацію стратегій, аналіз кампаній і моніторинг.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate("/pricing")}
                                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                                           bg-primary hover:bg-primary/90 text-white text-xs font-semibold"
                                data-testid="billing-go-pricing"
                            >
                                <span>Обрати тариф</span>
                                <ArrowRight size={12} />
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            {planMeta && (
                                <div className={`px-3 py-1.5 rounded-lg border ${planMeta.bg} ${planMeta.border}
                                                 font-display font-bold text-sm ${planMeta.color}`}
                                     data-testid="billing-plan-badge">
                                    {planMeta.name}
                                </div>
                            )}
                            {statusMeta && (
                                <div className={`inline-flex items-center gap-1.5 text-xs font-semibold ${statusMeta.color}`}
                                     data-testid="billing-status">
                                    <statusMeta.Icon size={14} />
                                    <span>{statusMeta.text}</span>
                                </div>
                            )}
                        </div>
                        {periodEnd && (
                            <div className="flex items-center gap-1.5 text-xs text-faint-c"
                                 data-testid="billing-period-end">
                                <Calendar size={12} />
                                <span>{isCancelling ? "Активна до" : "Наступний платіж"}: {periodEnd}</span>
                            </div>
                        )}
                    </div>

                    {isCancelling && (
                        <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs text-secondary-c"
                             data-testid="billing-cancelling-banner">
                            <span className="font-semibold text-warning-c">Підписку скасовано.</span>
                            {" "}Доступ зберігається до кінця оплаченого періоду.
                        </div>
                    )}

                    <div className="border-t border-border/60 pt-4">
                        <div className="text-xs uppercase tracking-wider font-mono text-faint-c mb-3">
                            Використання
                        </div>
                        {loading ? (
                            <div className="flex items-center gap-2 text-sm text-faint-c">
                                <Loader2 size={14} className="animate-spin" />
                                Завантаження…
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {bars.length === 0 ? (
                                    <div className="text-sm text-faint-c">Лімітів ще немає</div>
                                ) : bars.map(b => (
                                    <ProgressBar key={b.key} label={b.label} used={b.used} limit={b.limit} />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-t border-border/60 pt-4 flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => navigate("/pricing")}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                                       bg-primary/15 hover:bg-primary/25 border border-primary/30
                                       text-primary-c text-xs font-semibold transition-colors"
                            data-testid="billing-change-plan"
                        >
                            <CreditCard size={12} />
                            <span>Змінити тариф</span>
                        </button>
                        {!isCancelling && (
                            <button
                                type="button"
                                onClick={() => setConfirmCancel(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                                           border border-error/30 text-error-c
                                           hover:bg-error/10 text-xs font-semibold transition-colors"
                                data-testid="billing-cancel"
                            >
                                <span>Скасувати підписку</span>
                            </button>
                        )}
                    </div>
                </>
            )}

            <div className="text-[11px] text-faint-c leading-relaxed border-t border-border/60 pt-3">
                <span className="font-mono">ℹ️</span> Платежі обробляються через інтеграцію Emergent.
                Розширене керування підпискою (Customer Portal) з'явиться після переходу на власний платіжний акаунт.
            </div>

            <ConfirmModal
                open={confirmCancel}
                onClose={() => setConfirmCancel(false)}
                onConfirm={handleCancel}
                loading={cancelLoading}
                danger
                title="Скасувати підписку?"
                body={
                    <>
                        Підписка залишиться активною до <strong>{periodEnd || "кінця поточного періоду"}</strong>,
                        після чого доступ до функцій буде обмежено. Ви зможете відновити підписку
                        у будь-який момент.
                    </>
                }
                confirmLabel="Так, скасувати"
                cancelLabel="Залишити"
                testidPrefix="billing-cancel-modal"
            />
        </div>
    );
};

export default BillingSection;